import { useEffect, useMemo, useRef, useState } from 'react'
import {
  calculateAccountTradeFee,
  formatSuggestedFee,
  resolveFeeRuleForTrade,
  type AccountFeeConfig,
} from '../lib/accountFeeRules'
import { fetchFeeConfigsForAccounts } from '../lib/accountFeeRulesDb'
import { useAuth } from '../contexts/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNotification } from '../contexts/NotificationContext'
import { supabase } from '../lib/supabase'
import { QUICK_ADD_TRADE_MODAL_SOURCE } from '../lib/quickAddTradeModal'
import type { Trade } from '../types/trade'
import SymbolSelector from './SymbolSelector'
import LocalizedDateTimeInput from './LocalizedDateTimeInput'

const inputClass =
  'w-full px-2.5 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-gray-900'
const lockedInputClass =
  'w-full px-2.5 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed text-sm'
const labelClass = 'block text-xs font-medium text-gray-600 mb-1'

interface QuickAddTradeModalProps {
  onClose: () => void
  onSuccess?: () => void
  trade?: Trade | null
  appearance?: 'classic' | 'studio'
}

function toLocalDateTimeInputValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

function parseFeeInput(input: string): { isPercent: boolean; value: number } | null {
  const s = input.trim()
  if (!s) return null
  const isPercent = s.endsWith('%')
  const numStr = isPercent ? s.slice(0, -1) : s
  const value = parseFloat(numStr)
  if (Number.isNaN(value) || value < 0) return null
  return { isPercent, value }
}

export default function QuickAddTradeModal({ onClose, onSuccess, trade, appearance = 'classic' }: QuickAddTradeModalProps) {
  const isEditMode = Boolean(trade?.id)
  const isClosedTradeEdit = isEditMode && Boolean(trade?.exit_date)
  const { user, userProfile } = useAuth()
  const { canUseAccounts, membershipLimits } = useMembership()
  const { showWarning } = useNotification()

  const [tab, setTab] = useState<'general' | 'journal'>('general')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [market, setMarket] = useState<'Commodity' | 'Crypto' | 'Forex' | 'Stock'>(trade?.market ?? 'Stock')
  const [symbol, setSymbol] = useState(trade?.symbol ?? '')
  const [symbolExchange, setSymbolExchange] = useState<string | null>(trade?.exchange ?? null)
  const [longShort, setLongShort] = useState<'long' | 'short'>(trade?.long_short ?? 'long')
  const [quantity, setQuantity] = useState(trade?.quantity != null ? String(trade.quantity) : '')
  const [entryPrice, setEntryPrice] = useState(trade?.entry_price != null ? String(trade.entry_price) : '')
  const [feeInput, setFeeInput] = useState(trade?.fee != null ? String(trade.fee) : '0')
  const [takeProfit, setTakeProfit] = useState(trade?.take_profit != null ? String(trade.take_profit) : '')
  const [stopLoss, setStopLoss] = useState(trade?.stop_loss != null ? String(trade.stop_loss) : '')
  const [entryDate, setEntryDate] = useState(toLocalDateTimeInputValue(trade?.entry_date))
  const [strategyId, setStrategyId] = useState(trade?.strategy_id ?? '')
  const [accountId, setAccountId] = useState(trade?.account_id ?? '')
  const [strategies, setStrategies] = useState<Array<{ id: string; display_name: string }>>([])
  const [accounts, setAccounts] = useState<Array<{ id: string; platform: string; currency: string }>>([])
  const [strategiesLoading, setStrategiesLoading] = useState(true)
  const [accountsLoading, setAccountsLoading] = useState(true)
  const [accountFeeConfigs, setAccountFeeConfigs] = useState<Map<string, AccountFeeConfig>>(new Map())
  const feeInputTouchedRef = useRef(Boolean(trade?.fee != null))

  const [notes, setNotes] = useState(trade?.notes ?? '')
  const [tagsArray, setTagsArray] = useState<string[]>(trade?.tags ?? [])
  const [tagInputValue, setTagInputValue] = useState('')
  const [journalConfidence, setJournalConfidence] = useState(trade?.journal_confidence ?? 5)

  const [currency, setCurrency] = useState(trade?.currency?.trim() || 'USD')

  const [rr, setRr] = useState<number | null>(null)
  const currencySyncedFromProfile = useRef(false)

  useEffect(() => {
    if (currencySyncedFromProfile.current) return
    const c = userProfile?.preferred_currency?.trim()
    if (userProfile && c) {
      setCurrency(c)
      currencySyncedFromProfile.current = true
    }
  }, [userProfile])

  useEffect(() => {
    if (isEditMode) return
    const now = new Date()
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
    setEntryDate(localDateTime)
  }, [isEditMode])

  useEffect(() => {
    const entry = parseFloat(entryPrice)
    const stop = parseFloat(stopLoss)
    const tp = parseFloat(takeProfit)
    if (!entry || !stop || !tp || entry <= 0 || stop <= 0 || tp <= 0) {
      setRr(null)
      return
    }
    if (longShort === 'long') {
      if (entry <= stop || tp <= entry) {
        setRr(null)
        return
      }
      const risk = entry - stop
      const reward = tp - entry
      setRr(risk > 0 ? reward / risk : null)
    } else {
      if (entry >= stop || tp >= entry) {
        setRr(null)
        return
      }
      const risk = stop - entry
      const reward = entry - tp
      setRr(risk > 0 ? reward / risk : null)
    }
  }, [entryPrice, stopLoss, takeProfit, longShort])

  useEffect(() => {
    const loadStrategies = async () => {
      if (!user) return
      try {
        setStrategiesLoading(true)
        const { data, error: qErr } = await supabase
          .from('strategies')
          .select('id, display_name')
          .or(`user_id.eq.${user.id},is_public.eq.true`)
          .eq('is_active', true)
          .order('display_name', { ascending: true })
        if (qErr) throw qErr
        setStrategies(data || [])
      } catch (e) {
        console.error(e)
      } finally {
        setStrategiesLoading(false)
      }
    }
    loadStrategies()
  }, [user])

  useEffect(() => {
    const loadAccounts = async () => {
      if (!user || !canUseAccounts) {
        setAccountsLoading(false)
        return
      }
      try {
        setAccountsLoading(true)
        const { data, error: qErr } = await supabase
          .from('accounts')
          .select('id, platform, currency')
          .eq('user_id', user.id)
          .order('platform', { ascending: true })
        if (qErr) throw qErr
        const rows = data || []
        setAccounts(rows)
        if (rows.length) {
          try {
            const configs = await fetchFeeConfigsForAccounts(supabase, rows.map((row) => row.id))
            setAccountFeeConfigs(configs)
          } catch (configError) {
            console.error(configError)
            setAccountFeeConfigs(new Map())
          }
        } else {
          setAccountFeeConfigs(new Map())
        }
      } catch (e) {
        console.error(e)
        setAccounts([])
        setAccountFeeConfigs(new Map())
      } finally {
        setAccountsLoading(false)
      }
    }
    loadAccounts()
  }, [user, canUseAccounts])

  useEffect(() => {
    feeInputTouchedRef.current = false
  }, [accountId])

  useEffect(() => {
    setSymbolExchange(null)
  }, [market])

  useEffect(() => {
    if (feeInputTouchedRef.current || isClosedTradeEdit) return
    if (isEditMode && trade?.fee != null) return
    if (!accountId) return
    const config = accountFeeConfigs.get(accountId)
    const rule = resolveFeeRuleForTrade(config, market, symbolExchange)
    const qty = parseFloat(quantity)
    const entry = parseFloat(entryPrice)
    const notional = qty > 0 && entry > 0 ? qty * entry : 0
    const suggested = calculateAccountTradeFee(rule, notional)
    if (suggested != null) setFeeInput(formatSuggestedFee(suggested))
  }, [accountId, market, symbolExchange, quantity, entryPrice, accountFeeConfigs, isEditMode, isClosedTradeEdit, trade?.fee])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const submitFee = useMemo(() => {
    const qty = parseFloat(quantity)
    const entry = parseFloat(entryPrice)
    const entryAmount = qty > 0 && entry > 0 ? qty * entry : 0
    const parsed = parseFeeInput(feeInput)
    if (!parsed) return 0
    return parsed.isPercent ? (entryAmount * parsed.value) / 100 : parsed.value
  }, [feeInput, quantity, entryPrice])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!user) {
      setError('You must be signed in to save trades')
      return
    }
    if (!symbol.trim()) {
      setError('Symbol is required')
      return
    }
    if (!isClosedTradeEdit) {
      if (!entryPrice) {
        setError('Price is required')
        return
      }
      if (!quantity.trim() || parseFloat(quantity) <= 0) {
        setError('Quantity is required')
        return
      }
    }

    if (!isEditMode && membershipLimits?.max_trades_year !== null) {
      try {
        const now = new Date()
        const yearStart = new Date(now.getFullYear(), 0, 1)
        const yearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59)
        const { count, error: countError } = await supabase
          .from('trades')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .gte('created_at', yearStart.toISOString())
          .lte('created_at', yearEnd.toISOString())
        if (countError) throw countError
        const maxY = membershipLimits.max_trades_year
        if ((count || 0) >= maxY) {
          setError(`You have reached the maximum number of trades for this year (${maxY}).`)
          return
        }
      } catch (err) {
        console.error(err)
      }
    }

    if (!isEditMode && membershipLimits?.max_open_trades !== null) {
      try {
        const { count, error: countError } = await supabase
          .from('trades')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .is('exit_date', null)
        if (countError) throw countError
        const maxO = membershipLimits.max_open_trades
        if ((count || 0) >= maxO) {
          setError(`You have reached the maximum number of open trades (${maxO}).`)
          return
        }
      } catch (err) {
        console.error(err)
      }
    }

    const finalTags = [...tagsArray]
    if (tagInputValue.trim() && finalTags.length < 5) finalTags.push(tagInputValue.trim())

    setLoading(true)
    try {
      const qty = parseFloat(quantity)
      const entry = parseFloat(entryPrice)
      const positionSize = !isClosedTradeEdit && qty > 0 && entry > 0 ? qty * entry : null

      const baseData: Record<string, unknown> = {
        market,
        symbol: symbol.trim(),
        trade_type: 'spot',
        stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfit ? parseFloat(takeProfit) : null,
        notes: notes.trim() || null,
        strategy_id: strategyId || null,
        currency: currency.trim() || null,
        tags: finalTags.length > 0 ? finalTags.slice(0, 5) : null,
        rr,
        journal_confidence: journalConfidence,
      }

      if (!isClosedTradeEdit) {
        baseData.long_short = longShort
        baseData.entry_date = entryDate ? new Date(entryDate).toISOString() : null
        baseData.entry_price = entry
        baseData.quantity = qty
        baseData.contract_size = 1
        baseData.position_size = positionSize
        baseData.fee = submitFee > 0 ? submitFee : null
      }

      if (canUseAccounts) {
        baseData.account_id = accountId || null
      }

      if (isEditMode && trade) {
        const { error: updateError } = await supabase
          .from('trades')
          .update(baseData)
          .eq('id', trade.id)
          .eq('user_id', user.id)

        if (updateError) throw updateError
      } else {
        const insertData: Record<string, unknown> = {
          ...baseData,
          user_id: user.id,
          is_public: false,
          public_visibility: 'link',
          exit_date: null,
          trade_setup: null,
          entry_setup_image: null,
          exit_setup_image: null,
          time_interval: null,
          created_via: QUICK_ADD_TRADE_MODAL_SOURCE,
        }

        const { error: insertError } = await supabase
          .from('trades')
          .insert([insertData])
          .select('id, public_slug')
          .single()

        if (insertError) throw insertError
      }

      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save trade')
    } finally {
      setLoading(false)
    }
  }

  const studio = appearance === 'studio'
  const fieldClass = studio ? 'at-qt-input' : inputClass
  const lockedFieldClass = studio ? 'at-qt-input is-locked' : lockedInputClass
  const fieldLabelClass = studio ? 'at-qt-label' : labelClass

  return (
    <div
      className={studio ? 'at-quick-trade-backdrop' : 'fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50'}
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-add-trade-title"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) onClose()
      }}
    >
      <div className={studio ? 'at-quick-trade' : 'w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl border border-gray-200 bg-white shadow-xl overflow-hidden'}>
        <div className={studio ? 'at-qt-title' : 'flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0'}>
          <span>
            <h2 id="quick-add-trade-title" className={studio ? undefined : 'text-base font-semibold text-gray-900'}>
              {isEditMode ? 'Edit trade' : 'New trade'}
            </h2>
            {studio && <small>{isEditMode ? 'Update this journal entry.' : 'Record a position without leaving the dashboard.'}</small>}
          </span>
          <button
            type="button"
            onClick={onClose}
            className={studio ? '' : 'p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors'}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={studio ? 'at-qt-tabs' : 'flex gap-4 px-4 border-b border-gray-200 shrink-0'}>
          <button
            type="button"
            onClick={() => setTab('general')}
            className={studio ? (tab === 'general' ? 'is-active' : '') : `py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'general' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            General
          </button>
          <button
            type="button"
            onClick={() => setTab('journal')}
            className={studio ? (tab === 'journal' ? 'is-active' : '') : `py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'journal' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Journal
          </button>
        </div>

        <form onSubmit={handleSubmit} className={studio ? 'at-qt-form' : 'flex flex-col flex-1 min-h-0'}>
          <div className={studio ? 'at-qt-body' : 'overflow-y-auto flex-1 px-4 py-3'}>
            {error && (
              <div className={studio ? 'at-qt-error' : 'mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2'}>{error}</div>
            )}

            {isClosedTradeEdit && (
              <div className={studio ? 'at-qt-note' : 'mb-3 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2'}>
                Entry and exit values are locked for closed trades. Use full edit to change them.
              </div>
            )}

            {tab === 'general' && (
              <div className="space-y-3">
                {/* Row 1 */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  <div>
                    <label className={fieldLabelClass}>Market</label>
                    <select
                      value={market}
                      onChange={(e) => setMarket(e.target.value as typeof market)}
                      className={fieldClass}
                    >
                      <option value="Commodity">Commodity</option>
                      <option value="Crypto">Crypto</option>
                      <option value="Forex">Forex</option>
                      <option value="Stock">Stock</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2 md:col-span-1">
                    <label className={fieldLabelClass}>Symbol</label>
                    <SymbolSelector
                      market={market}
                      value={symbol}
                      onChange={(nextSymbol, symbolData) => {
                        setSymbol(nextSymbol)
                        setSymbolExchange(symbolData?.exchange ?? null)
                      }}
                      required
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label className={fieldLabelClass}>Quantity</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      disabled={isClosedTradeEdit}
                      className={isClosedTradeEdit ? lockedFieldClass : fieldClass}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className={fieldLabelClass}>Price</label>
                    <input
                      type="number"
                      step="any"
                      value={entryPrice}
                      onChange={(e) => setEntryPrice(e.target.value)}
                      disabled={isClosedTradeEdit}
                      className={isClosedTradeEdit ? lockedFieldClass : fieldClass}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 md:col-span-1">
                    <label className={fieldLabelClass}>Fee</label>
                    <input
                      type="text"
                      value={feeInput}
                      onChange={(e) => {
                        feeInputTouchedRef.current = true
                        setFeeInput(e.target.value)
                      }}
                      disabled={isClosedTradeEdit}
                      className={isClosedTradeEdit ? lockedFieldClass : fieldClass}
                      placeholder="0 or 0.5%"
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className={fieldLabelClass}>Take profit</label>
                    <input
                      type="number"
                      step="any"
                      value={takeProfit}
                      onChange={(e) => setTakeProfit(e.target.value)}
                      className={fieldClass}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className={fieldLabelClass}>Stop loss</label>
                    <input
                      type="number"
                      step="any"
                      value={stopLoss}
                      onChange={(e) => setStopLoss(e.target.value)}
                      className={fieldClass}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className={fieldLabelClass}>Currency</label>
                    <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={fieldClass}>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="TRY">TRY</option>
                      <option value="SEK">SEK</option>
                      <option value="DKK">DKK</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  <div>
                    <label className={fieldLabelClass}>Side</label>
                    <div
                      className={studio
                        ? `at-qt-side ${isClosedTradeEdit ? 'is-locked' : ''}`
                        : `flex rounded-lg border border-gray-300 overflow-hidden h-[38px] ${isClosedTradeEdit ? 'opacity-60 pointer-events-none bg-gray-50' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={() => setLongShort('long')}
                        className={studio
                          ? longShort === 'long' ? 'is-long' : ''
                          : `flex-1 text-sm font-medium transition-colors ${longShort === 'long' ? 'bg-green-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Long
                      </button>
                      <button
                        type="button"
                        onClick={() => setLongShort('short')}
                        className={studio
                          ? longShort === 'short' ? 'is-short' : ''
                          : `flex-1 text-sm font-medium transition-colors border-l border-gray-300 ${longShort === 'short' ? 'bg-red-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Short
                      </button>
                    </div>
                  </div>
                </div>

                {/* Row 3 */}
                <div
                  className={`grid grid-cols-1 gap-3 ${canUseAccounts ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
                >
                  <div>
                    <label className={fieldLabelClass}>Entry date & time</label>
                    <LocalizedDateTimeInput
                      value={entryDate}
                      onChange={setEntryDate}
                      disabled={isClosedTradeEdit}
                      className={isClosedTradeEdit ? lockedFieldClass : fieldClass}
                    />
                  </div>
                  <div>
                    <label className={fieldLabelClass}>Strategy</label>
                    <select value={strategyId} onChange={(e) => setStrategyId(e.target.value)} className={fieldClass}>
                      <option value="">Optional</option>
                      {strategiesLoading ? (
                        <option disabled value="__loading__">
                          Loading…
                        </option>
                      ) : (
                        strategies.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.display_name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  {canUseAccounts && (
                    <div>
                      <label className={fieldLabelClass}>Account</label>
                      <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={fieldClass}>
                        <option value="">Optional</option>
                        {accountsLoading ? (
                          <option disabled value="__loading__">
                            Loading…
                          </option>
                        ) : (
                          accounts.map((acc) => (
                            <option key={acc.id} value={acc.id}>
                              {acc.platform} ({acc.currency})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                </div>

                {isClosedTradeEdit && trade && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-gray-100">
                    <div>
                      <label className={fieldLabelClass}>Exit date & time</label>
                      <input
                        type="text"
                        value={toLocalDateTimeInputValue(trade.exit_date) || '—'}
                        disabled
                        readOnly
                        className={lockedFieldClass}
                      />
                    </div>
                    <div>
                      <label className={fieldLabelClass}>Exit price</label>
                      <input
                        type="text"
                        value={trade.exit_price != null ? String(trade.exit_price) : '—'}
                        disabled
                        readOnly
                        className={lockedFieldClass}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'journal' && (
              <div className="space-y-3 max-w-xl">
                <div>
                  <label className={fieldLabelClass}>Tags</label>
                  <div className={studio ? 'at-qt-tags' : 'flex flex-wrap gap-1.5 items-center px-2.5 py-1.5 border border-gray-300 rounded-lg bg-white min-h-[38px]'}>
                    {tagsArray.map((tag) => (
                      <span
                        key={tag}
                        className={studio ? 'at-qt-tag' : 'inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-orange-50 text-orange-800 rounded text-xs font-medium'}
                      >
                        #{tag}
                        <button
                          type="button"
                          onClick={() => setTagsArray((prev) => prev.filter((x) => x !== tag))}
                          className="hover:bg-orange-100 rounded p-0.5"
                          aria-label={`Remove ${tag}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={tagInputValue}
                      onChange={(e) => setTagInputValue(e.target.value)}
                      disabled={tagsArray.length >= 5}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault()
                          const word = tagInputValue.trim()
                          if (word && !tagsArray.includes(word)) {
                            if (tagsArray.length >= 5) showWarning('Maximum 5 tags allowed.')
                            else {
                              setTagsArray((prev) => [...prev, word])
                              setTagInputValue('')
                            }
                          }
                        } else if (e.key === 'Backspace' && !tagInputValue && tagsArray.length > 0) {
                          setTagsArray((prev) => prev.slice(0, -1))
                        }
                      }}
                      onBlur={() => {
                        const word = tagInputValue.trim()
                        if (word && !tagsArray.includes(word)) {
                          if (tagsArray.length >= 5) showWarning('Maximum 5 tags allowed.')
                          else {
                            setTagsArray((prev) => [...prev, word])
                            setTagInputValue('')
                          }
                        }
                      }}
                      className="flex-1 min-w-[100px] outline-none bg-transparent text-sm text-gray-900 placeholder:text-gray-400"
                      placeholder={tagsArray.length >= 5 ? 'Max 5 tags' : 'Add tags, Space to add'}
                    />
                  </div>
                </div>
                <div>
                  <label className={fieldLabelClass}>Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className={studio ? 'at-qt-input at-qt-notes' : `${inputClass} resize-none`}
                    placeholder="Trade notes…"
                  />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className={studio ? 'at-qt-label' : `${labelClass} mb-0`}>Confidence (1–10)</label>
                    <span className={studio ? 'at-qt-confidence' : 'text-xs font-medium text-orange-600'}>{journalConfidence}</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    step={1}
                    value={journalConfidence}
                    onChange={(e) => setJournalConfidence(Number(e.target.value))}
                    className={studio ? 'at-qt-range' : 'w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500'}
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span>1</span>
                    <span>10</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={studio ? 'at-qt-actions' : 'flex justify-end gap-2 px-4 py-3 border-t border-gray-200 shrink-0 bg-gray-50/80'}>
            {studio && (
              <span className="at-qt-rr">
                <small>Reward / risk</small>
                <strong>{rr != null ? rr.toFixed(2) : '—'}</strong>
              </span>
            )}
            <span className={studio ? undefined : 'flex gap-2'}>
              <button
                type="button"
                onClick={onClose}
                className={studio ? '' : 'px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-white transition-colors'}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={studio ? 'is-primary' : 'px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium transition-colors'}
              >
                {loading ? 'Saving…' : isEditMode ? 'Update' : 'Save'}
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  )
}
